import Cookies from 'js-cookie'
import { wsTrace, wsDrift } from '@/utils/wsDebugTrace'

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function getOrCreateInstanceId() {
  let id = sessionStorage.getItem('cosmoclaw_instance_id')
  if (!id) {
    id = generateUUID()
    sessionStorage.setItem('cosmoclaw_instance_id', id)
  }
  return id
}

const SILENT_TOKENS = ['NO_REPLY', 'NO_RE', 'HEARTBEAT_OK', '[questioning-action]']
const SILENT_PREFIXES = []
SILENT_TOKENS.forEach((token) => {
  for (let i = 1; i <= token.length; i++) {
    SILENT_PREFIXES.push(token.substring(0, i))
  }
})

function isPureSilentDelta(text) {
  return SILENT_TOKENS.some((t) => text === t)
}

function isSilentTokenPrefix(text) {
  return SILENT_PREFIXES.some((p) => text === p)
}

function stripSilentMarkers(text) {
  let result = text
  SILENT_TOKENS.forEach((token) => {
    while (result.includes(token)) {
      result = result.replace(token, '')
    }
  })
  return result.trim()
}

// Compare two WS endpoint URLs ignoring the `t=<token>` query param, so a token
// rotation does not count as an endpoint change (no need to rebuild the socket).
function sameWsEndpoint(a, b) {
  if (!a || !b) return a === b
  const strip = (u) => String(u).replace(/[?&]t=[^&]*/g, '')
  return strip(a) === strip(b)
}

// Replace the `t=<token>` query param in a WS URL with a fresh token.
function replaceUrlToken(url, token) {
  if (!url || !token) return url
  const encoded = encodeURIComponent(token)
  return String(url).replace(/[?&]t=[^&]*/g, (m) => m.charAt(0) + 't=' + encoded)
}

const CONNECTION_TIMEOUT_MS = 15000

class CosmoClawWebSocket {
  constructor(store, agentId) {
    this.store = store
    this.agentId = agentId || 'default'
    this.socket = null
    this.pendingRequests = new Map()
    this.reconnectTimer = null
    this.accessUrl = null
    this.token = null
    this._connectionId = 0
    this._connectTimer = null
    this._connectSent = false
    /**
     * 按会话独立追踪流式状态，防止会话切换时 _lastProcessedText 串用
     * sessionKey → { lastProcessedText: String|null }
     */
    this._sessionStreamState = {}
    /**
     * 临时路由覆盖 — 当收到非当前 agent 的事件时，
     * 指向该 session 所属的 agentId，使 _scopedCommit / _scopedDispatch / _agentState 路由到正确位置。
     * 仅在当前 _handleAgentEvent 调用期间有效。
     */
    this._routingAgent = null
    /**
     * 在途 stream run 归属：{ agentId, sessionKey, runId }。
     * 用于事件未携带 sessionKey 时（部分流式增量/回包），把内容路由回真正所属的数字人/智能体，
     * 防止切换数字人/智能体后，上一个会话的流式输出写入新打开页面的会话。
     */
    this._streamOwner = null
    /**
     * In-flight stream run ownership map: runId -> { agentId, sessionKey, lastActive, ended }.
     * More precise than single _streamOwner: events carrying runId are routed back to the
     * exact agent; ended runs are kept for a while so late residual deltas never leak into
     * a newly opened chat page of another agent.
     */
    this._streamRuns = new Map()
    /**
     * 最近一次发起 chat.send 的会话归属：{ sessionKey, agentId, ts }。
     * 用于事件未携带 sessionKey 且 runId 尚未被追踪时，把 run 归属到真正发起发送的会话，
     * 避免「发送后立即切换/新建会话」导致旧 run 首帧被错误归属到新会话。
     */
    this._lastSend = null
    /**
     * 已中止的 run：runId -> 时间戳。
     * 中止后留在原会话继续提问时，旧 run 的迟到事件（增量/工具/生命周期）一律丢弃，
     * 避免残留内容污染同一会话里新一轮的回答。
     */
    this._abortedRuns = new Map()
  }

  /**
   * 动态修改 agentId，用于共享 WS 连接时切换路由目标
   */
  setAgentId(id) {
    const prev = this.agentId
    this.agentId = id
    wsTrace('ws.setAgentId', {
      prev,
      next: id,
      storeAgentId: this.store.state.cosmoClaw.currentAgentId
    })
  }

  /**
   * 兼容旧接口 — 通过 agentName 查找对应的 agentId
   */
  setAgentName(name) {
    // 注意：此接口仅在 store 的 connectAgent 中旧代码使用，
    // 新代码应直接使用 setAgentId
    this.agentId = name
  }

  /**
   * 记录最近一次 chat.send 的会话归属（发送侧调用），
   * 供 _resolveEventOwner 在 runId 尚未被追踪时推断 run 的真正归属会话
   */
  recordSend(sessionKey, agentId, runId) {
    this._lastSend = {
      sessionKey: sessionKey || null,
      agentId: agentId || null,
      runId: runId || null,
      ts: Date.now()
    }
  }

  get _effectiveAgent() {
    return this._routingAgent || this.agentId
  }

  get _isCurrentAgent() {
    // 当 _routingAgent 有值时，说明正在处理非当前会话的事件，
    // 即使智能体相同也不应视为"当前"，防止流式内容写入错误的会话
    if (this._routingAgent) return false
    return this._effectiveAgent === this.store.state.cosmoClaw.currentAgentId
  }

  get _agentState() {
    const s = this.store.state.cosmoClaw
    if (this._isCurrentAgent) return s
    const ad = s.agentData[this._effectiveAgent] || {}
    const sk = ad.currentSessionKey
    return {
      localDemoMode: false,
      currentSessionKey: sk || null,
      currentRunId: (sk && ad.sessionRunIds && ad.sessionRunIds[sk]) || null,
      isAborting: ad.isAborting || false,
      isStreaming:
        (sk && ad.sessionIsStreaming && ad.sessionIsStreaming[sk]) || false,
      isFinishing: ad.isFinishing || false,
      messages: (sk && ad.sessionMessages && ad.sessionMessages[sk]) || [],
      agentEnded:
        (sk && ad.sessionAgentEnded && ad.sessionAgentEnded[sk]) || false,
      globalError: ad.globalError || null,
      lastAssistantText: ad.lastAssistantText || ''
    }
  }

  _getSessionState(sessionKey) {
    const s = this.store.state.cosmoClaw
    const ad = s.agentData[this._effectiveAgent]
    if (!ad) return null
    const sk = sessionKey || ad.currentSessionKey || ''
    return {
      localDemoMode: false,
      currentSessionKey: sk || null,
      currentRunId: (sk && ad.sessionRunIds && ad.sessionRunIds[sk]) || null,
      isAborting: ad.isAborting || false,
      isStreaming:
        (sk && ad.sessionIsStreaming && ad.sessionIsStreaming[sk]) || false,
      isFinishing: ad.isFinishing || false,
      messages: (sk && ad.sessionMessages && ad.sessionMessages[sk]) || [],
      agentEnded:
        (sk && ad.sessionAgentEnded && ad.sessionAgentEnded[sk]) || false,
      globalError: ad.globalError || null,
      lastAssistantText: ad.lastAssistantText || ''
    }
  }

  _scopedCommit(mutation, payload, extra = {}) {
    // 先更新对应 agent 的 agentData
    this.store.commit('cosmoClaw/AGENT_MUTATION', {
      agentId: this._effectiveAgent,
      mutation,
      payload,
      _routingActive: !!this._routingAgent,
      ...extra
    })
    // 连接状态（socketState / isHandshaked）是全局 WS 级别的，不受 agent 切换影响，
    // 必须始终同步到 flat state，否则切换 agent 后 UI 可能卡在「正在连接」状态。
    if (mutation === 'SET_SOCKET_STATE' || mutation === 'SET_IS_HANDSHAKED') {
      this.store.commit(`cosmoClaw/${mutation}`, payload)
    }
  }

  _scopedDispatch(action, payload = {}) {
    const targetAgent = this._effectiveAgent
    // 当 _routingAgent 有值时，即使目标是当前 agent 也要传 _agentId，
    // 确保 finishStreamingMessage 等 action 操作的是路由目标的 agentData，而非 flat state
    if (
      this._routingAgent ||
      targetAgent !== this.store.state.cosmoClaw.currentAgentId
    ) {
      return this.store.dispatch(`cosmoClaw/${action}`, {
        ...payload,
        _agentId: targetAgent
      })
    }
    return this.store.dispatch(`cosmoClaw/${action}`, payload)
  }

  /**
   * 根据 sessionKey 在 agentData 中查找所属的 agentId
   * agentData 已改为以 agentId 为 key，可直接从 sessionKey 中提取 agentId 进行查找
   */
  _resolveAgentBySessionKey(sessionKey) {
    const match = sessionKey && sessionKey.match(/^agent:([^:]+):/)
    if (match && match[1]) {
      const agentId = match[1]
      const agentData = this.store.state.cosmoClaw.agentData
      if (agentData[agentId]) return agentId
    }
    return null
  }

  /**
   * 判断当前会话是否为「活跃」会话：
   * - 有消息（含刚发送的用户消息）或正在流式输出，视为活跃；
   * - 刚新建的空初始页（无消息、未流式）视为非活跃。
   * 用于 runId 尚未被追踪 / run 归属缺失时，决定是否允许把事件归属到当前会话。
   */
  _isCurrentSessionLive(state, curSessionKey) {
    if (!curSessionKey) return false
    if (!state) return false
    const msgs = state.messages
    if (Array.isArray(msgs) && msgs.length > 0) return true
    if (state.isStreaming) return true
    return false
  }

  /**
   * 查找指定 agent 下最近活跃且已明确归属会话（带 sessionKey）的 run。
   * 用于 run 归属缺失时尽量把残留事件路由回其真正所属会话。
   */
  _findLatestSessionRun(agentId) {
    if (!agentId) return null
    let latest = null
    this._streamRuns.forEach((run) => {
      if (run.agentId === agentId && run.sessionKey) {
        if (!latest || run.lastActive > latest.lastActive) {
          latest = run
        }
      }
    })
    return latest || null
  }

  /**
   * 判断当前会话是否可被「无归属标识」的事件正向认领：
   * - 最近一次 chat.send 就发生在当前会话（60s 内）—— 新发起 run 的首帧；
   * - 或当前会话活跃（已有消息/正在流式输出）。
   * 刚新建的空初始页（无消息、未流式、最近未发送）一律不可认领，
   * 防止上一会话未结束的流式内容被写入新打开的初始页（串页）。
   */
  _canClaimCurrentSession(state, curSessionKey) {
    if (!curSessionKey) return false
    const sendInfo = this._lastSend
    if (
      sendInfo &&
      sendInfo.sessionKey === curSessionKey &&
      Date.now() - sendInfo.ts < 60000
    ) {
      return true
    }
    return this._isCurrentSessionLive(state, curSessionKey)
  }

  /**
   * 对无 sessionKey / 未追踪 runId 等无法正向识别的流式/回包事件推断归属。
   * 核心原则：宁丢不串页 —— 当前会话不可被认领时，优先路由回该 agent
   * 最近活跃且带 sessionKey 的 run（或 _streamOwner 明确归属的会话）；
   * 仍无法归属则丢弃，绝不写入刚新建的空初始页。
   */
  _resolveUnownedOwner(state, curSessionKey, fallbackAgentId, runId) {
    const hasRunId = !!runId
    // 事件携带 runId 时：只有 runId 与当前会话的 currentRunId（或最近一次发送携带的 runId）
    // 精确一致，才允许把事件归属到当前会话。旧会话残留的 runId 若凭 lastSend/live 盲认
    // 当前会话，会被写入新打开的初始页 → 串页，因此必须先用 runId 正向确认。
    if (hasRunId) {
      const currentRunIdStr =
        (state && state.currentRunId && String(state.currentRunId)) || null
      const sendInfo0 = this._lastSend
      const lastSendRunMatches =
        sendInfo0 &&
        sendInfo0.sessionKey === curSessionKey &&
        sendInfo0.runId &&
        String(sendInfo0.runId) === String(runId)
      if (
        (currentRunIdStr && currentRunIdStr === String(runId)) ||
        lastSendRunMatches
      ) {
        // runId 正向确认属于当前会话
        return {
          agentId: null,
          sessionKey: curSessionKey,
          routed: false,
          drop: false
        }
      }
      // runId 明确但不属于当前会话：优先路由到最近一次发送的会话（若属于其它会话）；
      // 否则路由到该 agent 最近带 sessionKey 的 run；仍无法归属则丢弃（宁丢不串页）
      const sendInfo = this._lastSend
      if (
        sendInfo &&
        sendInfo.sessionKey &&
        sendInfo.sessionKey !== curSessionKey &&
        Date.now() - sendInfo.ts < 60000
      ) {
        const agentOfSend =
          sendInfo.agentId ||
          this._resolveAgentBySessionKey(sendInfo.sessionKey)
        if (agentOfSend) {
          return {
            agentId: agentOfSend,
            sessionKey: sendInfo.sessionKey,
            routed: true,
            drop: false
          }
        }
        return {
          agentId: null,
          sessionKey: sendInfo.sessionKey,
          routed: true,
          drop: true
        }
      }
      const agentId =
        fallbackAgentId ||
        (this._streamOwner && this._streamOwner.agentId) ||
        null
      if (agentId) {
        const latest = this._findLatestSessionRun(agentId)
        if (latest && latest.sessionKey) {
          return {
            agentId: latest.agentId,
            sessionKey: latest.sessionKey,
            routed: true,
            drop: false
          }
        }
        if (
          this._streamOwner &&
          this._streamOwner.sessionKey &&
          this._streamOwner.sessionKey !== curSessionKey
        ) {
          return {
            agentId: this._streamOwner.agentId,
            sessionKey: this._streamOwner.sessionKey,
            routed: true,
            drop: false
          }
        }
      }
      return {
        agentId: null,
        sessionKey: null,
        routed: false,
        drop: true
      }
    }
    // 1) 最近一次发送的会话：可区分「切换后旧 run 首帧迟到」与「当前会话新发起的 run」
    const sendInfo = this._lastSend
    if (
      sendInfo &&
      sendInfo.sessionKey &&
      Date.now() - sendInfo.ts < 60000
    ) {
      if (sendInfo.sessionKey === curSessionKey) {
        return {
          agentId: null,
          sessionKey: curSessionKey,
          routed: false,
          drop: false
        }
      }
      const agentOfSend =
        sendInfo.agentId ||
        this._resolveAgentBySessionKey(sendInfo.sessionKey)
      if (agentOfSend) {
        return {
          agentId: agentOfSend,
          sessionKey: sendInfo.sessionKey,
          routed: true,
          drop: false
        }
      }
      return {
        agentId: null,
        sessionKey: sendInfo.sessionKey,
        routed: true,
        drop: true
      }
    }
    // 2) 当前会话活跃且无其它证据：视为当前会话
    if (this._isCurrentSessionLive(state, curSessionKey)) {
      return {
        agentId: null,
        sessionKey: curSessionKey,
        routed: false,
        drop: false
      }
    }
    // 3) 路由回该 agent 最近活跃且带 sessionKey 的 run（旧会话残留）
    const agentId =
      fallbackAgentId ||
      (this._streamOwner && this._streamOwner.agentId) ||
      null
    if (agentId) {
      const latest = this._findLatestSessionRun(agentId)
      if (latest && latest.sessionKey) {
        return {
          agentId: latest.agentId,
          sessionKey: latest.sessionKey,
          routed: true,
          drop: false
        }
      }
      // _streamOwner 有明确归属会话且不是当前空初始页：路由回其归属
      if (
        this._streamOwner &&
        this._streamOwner.sessionKey &&
        this._streamOwner.sessionKey !== curSessionKey
      ) {
        return {
          agentId: this._streamOwner.agentId,
          sessionKey: this._streamOwner.sessionKey,
          routed: true,
          drop: false
        }
      }
    }
    // 4) 无法归属：丢弃（宁丢不串页）
    return {
      agentId: null,
      sessionKey: null,
      routed: false,
      drop: true
    }
  }

  /**
   * 解析事件应归属的 agent 与会话（路由 + 会话归属一体）：
   * - 事件携带 sessionKey：当前会话 → {routed:false}；其它会话 → 按前缀解析 agent
   * - 事件携带 runId：按 _streamRuns 精确归属（已结束的 run 也保留，残留事件仍路由回原会话）
   * - 无 sessionKey/runId：优先当前会话的活跃 run；否则按最近活跃（含刚结束）的旧 run 归属，
   *   避免旧会话的流式/回包内容写入新打开页面的会话
   * - 刚新建的空初始页（非活跃、最近未发送）绝不接收任何无法正向归属的事件（宁丢不串页）
   * @returns {{ agentId: string|null, sessionKey: string|null, routed: boolean, drop: boolean }}
   */
  _resolveEventOwner(payload, state) {
    const curSessionKey = (state && state.currentSessionKey) || null
    if (!payload) {
      return { agentId: null, sessionKey: null, routed: false, drop: false }
    }
    // 1) 显式 sessionKey：当前会话 → 不路由；其它会话 → 按前缀解析归属 agent
    if (payload.sessionKey) {
      if (payload.sessionKey === curSessionKey) {
        return {
          agentId: null,
          sessionKey: payload.sessionKey,
          routed: false,
          drop: false
        }
      }
      const resolved = this._resolveAgentBySessionKey(payload.sessionKey)
      if (resolved) {
        return {
          agentId: resolved,
          sessionKey: payload.sessionKey,
          routed: true,
          drop: false
        }
      }
      return {
        agentId: null,
        sessionKey: payload.sessionKey,
        routed: true,
        drop: true
      }
    }
    // 2) runId → 精确 run 归属（已结束的 run 保留，残留事件仍路由回原会话）
    if (payload.runId) {
      const runIdStr = String(payload.runId)
      const run = this._streamRuns.get(runIdStr)
      if (run && run.agentId) {
        if (run.sessionKey) {
          // run 已明确归属会话：与会话级匹配一致 —— 当前会话不路由，其它会话路由回其归属
          if (run.sessionKey === curSessionKey) {
            return {
              agentId: null,
              sessionKey: run.sessionKey,
              routed: false,
              drop: false
            }
          }
          return {
            agentId: run.agentId,
            sessionKey: run.sessionKey,
            routed: true,
            drop: false
          }
        }
        // run.sessionKey 为空：不能仅凭 agentId 相同就判定为「当前会话」。
        // 同一数字人/智能体下重新进入初始页新建会话后，旧 run 的残留事件
        // （agentId 相同但会话归属未知）若被当作当前会话，会被打上新会话 key
        // 写入新页面 → 串页。仅在能正向确认该 run 属于当前会话时才不路由：
        // 1) 当前会话明确关联了该 runId（state.currentRunId 匹配）且会话活跃
        // 2) 最近一次发送就在当前会话且携带该 runId（如 silent 发送未写入 currentRunId）
        const sendInfo = this._lastSend
        const lastSendRunMatches =
          sendInfo &&
          sendInfo.sessionKey === curSessionKey &&
          sendInfo.runId &&
          String(sendInfo.runId) === runIdStr
        if (
          state &&
          state.currentRunId &&
          String(state.currentRunId) === runIdStr &&
          this._isCurrentSessionLive(state, curSessionKey)
        ) {
          return {
            agentId: null,
            sessionKey: curSessionKey,
            routed: false,
            drop: false
          }
        }
        if (lastSendRunMatches) {
          return {
            agentId: null,
            sessionKey: curSessionKey,
            routed: false,
            drop: false
          }
        }
        // 归属无法正向确认：优先路由到该 agent 最近带 sessionKey 的 run；
        // 找不到则丢弃，避免旧会话残留写入新打开的初始页
        const fallback = this._findLatestSessionRun(run.agentId)
        if (fallback && fallback.sessionKey) {
          return {
            agentId: fallback.agentId,
            sessionKey: fallback.sessionKey,
            routed: true,
            drop: false
          }
        }
        return {
          agentId: null,
          sessionKey: null,
          routed: false,
          drop: true
        }
      }
      // 未追踪的 runId：交给统一的无归属推断
      // （run 几乎必然由最近一次 chat.send 触发，可区分「切换后旧 run 首帧迟到」
      //   与「当前会话新发起的 run」；无法确认时宁丢不串页）
      return this._resolveUnownedOwner(state, curSessionKey, null, runIdStr)
    }
    // 3) 无 sessionKey / 无 runId：扫描最近活跃 run
    let currentRun = null
    let otherRun = null
    this._streamRuns.forEach((run) => {
      if (!run.agentId || !run.sessionKey) return
      if (run.sessionKey === curSessionKey) {
        if (!currentRun || run.lastActive > currentRun.lastActive) {
          currentRun = run
        }
      } else {
        if (!otherRun || run.lastActive > otherRun.lastActive) {
          otherRun = run
        }
      }
    })
    // 当前会话存在活跃 run 时，无归属事件优先视为当前会话
    if (currentRun && !currentRun.ended) {
      return {
        agentId: null,
        sessionKey: currentRun.sessionKey || curSessionKey,
        routed: false,
        drop: false
      }
    }
    if (otherRun) {
      // 其它会话存在 run（含刚结束的 run，覆盖 lifecycle end 之后的 chat final 等回包）：
      // 默认路由回其归属，避免旧会话残留写入新打开的初始页；
      // 但若最近一次发送就在当前会话（当前会话刚发起新 run、首帧尚未被追踪），
      // 优先认领当前会话，避免把当前会话新 run 的无归属增量误路由到旧会话
      const sendInfo = this._lastSend
      // 仅当当前会话的发送时刻晚于其它 run 的最后活跃时刻时才认领当前会话：
      // 说明这是当前会话新 run 的首帧（旧 run 的活跃时间已被后续事件刷新），
      // 避免把旧会话（A）在发送之后仍在输出的无标识残留误认领为新会话（B）
      if (
        sendInfo &&
        sendInfo.sessionKey === curSessionKey &&
        Date.now() - sendInfo.ts < 60000 &&
        sendInfo.ts > otherRun.lastActive
      ) {
        return {
          agentId: null,
          sessionKey: curSessionKey,
          routed: false,
          drop: false
        }
      }
      return {
        agentId: otherRun.agentId,
        sessionKey: otherRun.sessionKey || null,
        routed: true,
        drop: false
      }
    }
    // 4) 单槽兜底（_streamOwner）：仅当其归属会话已知且不是「当前空初始页」时才路由；
    //    归属会话缺失或指向当前空初始页时交给统一推断（宁丢不串页），
    //    避免同 agent 下重新进入初始页新建会话后，旧 run 的残留事件写入新页面
    if (this._streamOwner && this._streamOwner.agentId) {
      if (
        this._streamOwner.sessionKey &&
        this._streamOwner.sessionKey !== curSessionKey
      ) {
        // 与步骤3一致：仅当当前会话的发送时刻晚于旧 owner 的活跃时刻，
        // 才视为当前会话新 run 的首帧并认领；否则路由回旧 owner 的归属会话
        const sendInfo = this._lastSend
        const sendAfterOwner =
          sendInfo &&
          sendInfo.sessionKey === curSessionKey &&
          Date.now() - sendInfo.ts < 60000 &&
          sendInfo.ts > (this._streamOwner.ts || 0)
        if (sendAfterOwner) {
          return {
            agentId: null,
            sessionKey: curSessionKey,
            routed: false,
            drop: false
          }
        }
        return {
          agentId: this._streamOwner.agentId,
          sessionKey: this._streamOwner.sessionKey,
          routed: true,
          drop: false
        }
      }
      if (this._streamOwner.sessionKey === curSessionKey) {
        // _streamOwner 归属当前会话：仅当前会话可被认领（活跃或最近发送）时才不路由
        if (this._canClaimCurrentSession(state, curSessionKey)) {
          return {
            agentId: null,
            sessionKey: curSessionKey,
            routed: false,
            drop: false
          }
        }
      }
      return this._resolveUnownedOwner(
        state,
        curSessionKey,
        this._streamOwner.agentId,
        null
      )
    }
    // 5) 无任何在途信息：统一推断（宁丢不串页）
    return this._resolveUnownedOwner(state, curSessionKey, null, null)
  }

  /**
   * 解析事件应路由到的 agentId（兼容旧调用）：
   * - 事件携带 sessionKey 且不属于当前会话时，按 sessionKey 前缀解析归属 agent
   * - 事件未携带 sessionKey 时，按在途 stream run（_streamRuns / _streamOwner）的归属 agent 路由
   * @returns {string|null|'__DROP__'} 路由目标；null 表示当前 agent；'__DROP__' 表示无法归属应丢弃
   */
  _resolveRoutingTarget(payload, state) {
    const owner = this._resolveEventOwner(payload, state)
    if (owner.drop) return '__DROP__'
    return owner.routed ? owner.agentId : null
  }

  /**
   * 解析事件实际所属的会话 sessionKey：
   * 优先取事件自带的 sessionKey；缺失时按 runId 查在途 run 归属；
   * 仍缺失则回退到最近一次活跃流式会话（_streamRuns / _streamOwner）。
   * 用于 chat final/error 等兜底回包，确保旧会话内容写入其所属会话而非新页面。
   */
  _resolveEventSessionKey(payload) {
    if (!payload) return null
    if (payload.sessionKey) return payload.sessionKey
    if (payload.runId) {
      const run = this._streamRuns.get(String(payload.runId))
      if (run && run.sessionKey) return run.sessionKey
    }
    const owner = this._resolveEventOwner(payload, this.store.state.cosmoClaw)
    return owner.sessionKey || null
  }

  /**
   * 清除在途 stream run 归属（run 结束/中止时调用）
   */
  _clearStreamOwner(key) {
    if (!key) {
      // Full cleanup (e.g. disconnect): mark all runs ended and clear recent owner
      this._streamRuns.forEach((run) => {
        run.ended = true
      })
      this._streamOwner = null
      return
    }
    const sk = String(key)
    // Mark matching runs (by runId or sessionKey) ended instead of deleting immediately,
    // so late residual deltas can still be routed back to their original agent.
    const runByKey = this._streamRuns.get(sk)
    if (runByKey) {
      runByKey.ended = true
      runByKey.lastActive = Date.now()
    }
    this._streamRuns.forEach((run) => {
      if (run.sessionKey === sk) {
        run.ended = true
        run.lastActive = Date.now()
      }
    })
    if (
      this._streamOwner &&
      (this._streamOwner.sessionKey === sk || this._streamOwner.runId === sk)
    ) {
      this._streamOwner = null
    }
  }

  /**
   * 标记某次 run 已被中止：该 run 的迟到事件一律丢弃。
   * 中止只停止本轮输出，会话与已产出内容保留，因此必须挡住旧 run 的残留增量。
   */
  markRunAborted(runId) {
    if (!runId) return
    this._abortedRuns.set(String(runId), Date.now())
    const expireAt = Date.now() - 5 * 60 * 1000
    this._abortedRuns.forEach((ts, key) => {
      if (ts < expireAt) this._abortedRuns.delete(key)
    })
  }

  /**
   * 重置某会话的流式文本基准：中止后在同一会话继续提问时，
   * 新一轮的首个全量文本不会被当成旧轮文本的增量继续追加。
   */
  resetSessionStreamState(sessionKey) {
    const sk = sessionKey || (this._agentState && this._agentState.currentSessionKey)
    if (!sk) return
    if (this._sessionStreamState[sk]) {
      this._sessionStreamState[sk].lastProcessedText = null
    }
  }

  /**
   * Track the in-flight stream run ownership (agent + session) so later streaming events
   * without sessionKey can be routed back to the correct agent.
   */
  _trackStreamOwner(payload, sessionKey) {
    const ownerAgent = this._routingAgent || this._effectiveAgent
    const ownerSessionKey = payload.sessionKey || sessionKey || null
    if (payload.runId) {
      const rid = String(payload.runId)
      // Prune runs ended more than 30s ago to avoid unbounded growth
      const now = Date.now()
      this._streamRuns.forEach((run, key) => {
        if (run.ended && now - run.lastActive > 30000) {
          this._streamRuns.delete(key)
        }
      })
      const existing = this._streamRuns.get(rid)
      if (existing) {
        // 已有归属的 run 不覆盖 agentId/sessionKey（防止被误归属的事件污染），
        // 仅刷新活跃时间；归属为空时可用事件携带的 sessionKey 补全
        existing.lastActive = now
        if (!existing.sessionKey && ownerSessionKey) {
          existing.sessionKey = ownerSessionKey
        }
      } else {
        this._streamRuns.set(rid, {
          runId: rid,
          agentId: ownerAgent,
          sessionKey: ownerSessionKey,
          lastActive: now,
          ended: false
        })
      }
    }
    // Update most-recent active stream owner (fallback for events without runId).
    // 以 run 归属为准：run 已记录时用其归属，避免把旧 run 归属改成新会话
    if (payload.runId) {
      const run = this._streamRuns.get(String(payload.runId))
      if (run) {
        this._streamOwner = {
          agentId: run.agentId || ownerAgent,
          sessionKey: run.sessionKey || ownerSessionKey,
          runId: String(payload.runId),
          ts: Date.now()
        }
      }
    } else if (
      !this._streamOwner ||
      this._streamOwner.agentId !== ownerAgent ||
      (ownerSessionKey &&
        this._streamOwner.sessionKey !== ownerSessionKey)
    ) {
      this._streamOwner = {
        agentId: ownerAgent,
        sessionKey: ownerSessionKey,
        runId: null,
        ts: Date.now()
      }
    } else {
      this._streamOwner.ts = Date.now()
    }
  }

  connect(accessUrl, token) {
    if (!accessUrl) {
      console.warn('[WS] accessUrl 为空，跳过连接（tianzhi agent token 可能尚未就绪）')
      this._scopedCommit('SET_SOCKET_STATE', 'DISCONNECTED')
      return
    }

    // Fall back to the latest tianzhi token from cookie when no explicit token is
    // passed (connectShared is usually invoked with accessUrl only).
    if (!token) {
      const WS_TOKEN_KEY =
        process.env.VUE_APP_WS_TOKEN_KEY || 'tianzhi_test_agent_token'
      token = Cookies.get(WS_TOKEN_KEY) || this.token || ''
    } // 底层 socket 仍处于 OPEN 且目标 URL 未变化：连接实际可用，仅刷新 token，
    // 不重建底层连接（避免路由切换/重复保活时把正常连接断开重建）
    if (
      this.socket &&
      this.socket.readyState === WebSocket.OPEN &&
      sameWsEndpoint(accessUrl, this.accessUrl)
    ) {
      if (token) this.token = token
      // socket 可用但握手未完成（hello-ok 未收到）时补发 connect，
      // 不重建底层连接，避免 UI 卡在未连接状态
      if (!this.store.state.cosmoClaw.isHandshaked) {
        this._connectSent = false
        this.sendConnectProactive()
      }
      return
    }
    this.accessUrl = accessUrl
    if (token) this.token = token
    this._connectionId++
    this._connectSent = false
    // 复用实例重连/新连接时清除手动断开标记，避免断开后无法再自动重连
    this._manualDisconnect = false
    const connId = this._connectionId
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.socket) {
      this.socket.onopen = null
      this.socket.onmessage = null
      this.socket.onclose = null
      this.socket.onerror = null
      if (
        this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING
      ) {
        this.socket.close()
      }
      this.socket = null
    }

    let wsUrl
    if (/^wss?:\/\//i.test(accessUrl)) {
      wsUrl = accessUrl
    } else if (accessUrl.startsWith('/')) {
      // 相对路径 — 走代理转发
      wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${
        window.location.host
      }${accessUrl}`
    } else {
      // IP:端口格式 — 旧模式
      wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${
        window.location.host
      }/openclaw-ws/${accessUrl}`
    }

    console.log('正在连接 WebSocket:', wsUrl)
    this._scopedCommit('SET_SOCKET_STATE', 'CONNECTING')

    if (this._connectTimer) {
      clearTimeout(this._connectTimer)
      this._connectTimer = null
    }

    const socket = new WebSocket(wsUrl)
    this.socket = socket

    this._connectTimer = setTimeout(() => {
      if (connId !== this._connectionId) return
      console.warn(
        `WebSocket 连接超时 (${CONNECTION_TIMEOUT_MS}ms)，正在断开重试...`
      )
      this._scopedCommit('SET_SOCKET_STATE', 'DISCONNECTED')
      if (this.socket) {
        this.socket.onopen = null
        this.socket.onmessage = null
        this.socket.onclose = null
        this.socket.onerror = null
        if (
          this.socket.readyState === WebSocket.OPEN ||
          this.socket.readyState === WebSocket.CONNECTING
        ) {
          this.socket.close()
        }
        this.socket = null
      }
      this._connectTimer = null
      this._scheduleReconnect()
    }, CONNECTION_TIMEOUT_MS)

    socket.onopen = () => {
      if (connId !== this._connectionId) return
      if (this._connectTimer) {
        clearTimeout(this._connectTimer)
        this._connectTimer = null
      }
      // console.log('WebSocket 连接已打开，等待 connect.challenge...')
      wsTrace('ws.open', {
        wsAgentId: this.agentId,
        storeAgentId: this.store.state.cosmoClaw.currentAgentId
      })
      this._scopedCommit('SET_SOCKET_STATE', 'CONNECTED')
      // 主动发送 connect 请求：若服务端未下发 challenge 则自行发起
      this.sendConnectProactive()
    }

    socket.onmessage = (event) => {
      if (connId !== this._connectionId) return
      try {
        const data = JSON.parse(event.data)
        this._handleMessage(data)
      } catch (e) {
        console.error('解析消息失败:', e)
      }
    }

    socket.onclose = (event) => {
      if (connId !== this._connectionId) return
      console.log(
        'WebSocket 连接关闭:',
        event.code,
        event.reason,
        event.wasClean
      )
      this._scopedCommit('SET_SOCKET_STATE', 'DISCONNECTED')
      this._scopedCommit('SET_IS_HANDSHAKED', false)
      wsTrace('ws.close', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        wsAgentId: this.agentId,
        storeAgentId: this.store.state.cosmoClaw.currentAgentId
      })
      this._scheduleReconnect()
      this._manualDisconnect = false
    }

    socket.onerror = (error) => {
      if (connId !== this._connectionId) return
      console.error('WebSocket 连接错误:', error)
      this._scopedCommit('SET_SOCKET_STATE', 'DISCONNECTED')
      this._scopedCommit('SET_IS_HANDSHAKED', false)
      // error 不一定伴随 close 事件，这里主动调度自动重连，避免一直卡在「未连接」
      this._scheduleReconnect()
    }
  }

  _scheduleReconnect() {
    if (this._manualDisconnect || !this.accessUrl) return
    if (this.reconnectTimer) return
    console.log('连接意外断开，3秒后自动重连...')
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      // Reconnect with the freshest token: it may have been rotated by a 401
      // refresh or a re-registration; using a stale token fails handshake auth
      // forever, leaving the UI stuck in the disconnected state.
      const WS_TOKEN_KEY =
        process.env.VUE_APP_WS_TOKEN_KEY || 'tianzhi_test_agent_token'
      const freshToken = Cookies.get(WS_TOKEN_KEY) || this.token || ''
      const accessUrl = replaceUrlToken(this.accessUrl, freshToken)
      this.connect(accessUrl, freshToken)
    }, 3000)
  }

  disconnect() {
    this._manualDisconnect = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this._connectTimer) {
      clearTimeout(this._connectTimer)
      this._connectTimer = null
    }
    if (this.socket) {
      this.socket.onopen = null
      this.socket.onmessage = null
      this.socket.onclose = null
      this.socket.onerror = null
      this.socket.close()
      this.socket = null
    }
    this.pendingRequests.clear()
    this._scopedCommit('SET_SOCKET_STATE', 'DISCONNECTED')
    this._scopedCommit('SET_IS_HANDSHAKED', false)
  }

  sendRequest(method, params = {}) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('WebSocket 未连接'))
    }

    const id = generateUUID()
    const request = { type: 'req', id, method, params }

    return new Promise((resolve, reject) => {
      this.pendingRequests.set(id, { resolve, reject, method })
      this.socket.send(JSON.stringify(request))
    })
  }

  _handleMessage(data) {
    if (data.type === 'res') {
      this._handleResponse(data)
    } else if (data.type === 'event') {
      this._handleEvent(data)
    }
  }

  _handleResponse(data) {
    const { id, ok, payload, error } = data

    // 先按 id 结算 pendingRequests：后面的 hello-ok / aborted / completed 分支会提前 return，
    // 若放到后面会导致 Promise 永不 settle、pendingRequests 泄漏，await 的调用方被永久卡住
    let settled = false
    if (id) {
      const pending = this.pendingRequests.get(id)
      if (pending) {
        this.pendingRequests.delete(id)
        settled = true
        if (error) {
          pending.reject(new Error(error.message || '请求失败'))
        } else if (ok) {
          pending.resolve(payload)
        } else {
          pending.reject(new Error((payload && payload.message) || '请求失败'))
        }
      }
    }

    if (payload && payload.type === 'hello-ok') {
      wsTrace('ws.helloOk', {
        wsAgentId: this.agentId,
        storeAgentId: this.store.state.cosmoClaw.currentAgentId,
        inSync: this.agentId === this.store.state.cosmoClaw.currentAgentId
      })
      this._scopedCommit('SET_IS_HANDSHAKED', true)
      this._scopedDispatch('loadSessions', { lock: true })
      this._scopedDispatch('loadModels')
      this._scopedDispatch('startSessionPolling')
      return
    }

    if (payload && payload.aborted) {
      const respSessionKey = this._resolveEventSessionKey(payload)
      this._clearStreamOwner(respSessionKey)
      this._scopedDispatch('finishStreamingMessage', { _sessionKey: respSessionKey })
      return
    }
    if (payload && payload.summary === 'completed') {
      const respSessionKey = this._resolveEventSessionKey(payload)
      this._clearStreamOwner(respSessionKey)
      this._scopedDispatch('finishStreamingMessage', { _sessionKey: respSessionKey })
      return
    }

    if (error) {
      console.error('请求错误:', error)
      if (error.details && error.details.authReason === 'token_mismatch') {
        this._scopedCommit('SET_SOCKET_STATE', 'FAILED')
      }
      return
    }

    // 已按 id 结算过的响应不再走下面的兜底匹配，避免重复处理
    if (settled) return

    if (ok && payload) {
      let matchedMethod = null
      if (payload.sessions) matchedMethod = 'sessions.list'
      else if (payload.models) matchedMethod = 'models.list'
      else if (payload.messages) matchedMethod = 'chat.history'

      if (matchedMethod) {
        for (const [reqId, pending] of this.pendingRequests.entries()) {
          if (pending.method === matchedMethod) {
            this.pendingRequests.delete(reqId)
            pending.resolve(payload)
            return
          }
        }
      }
    }
  }

  _handleEvent(data) {
    const { event, payload } = data

    if (event === 'tick' || event === 'heartbeat' || event === 'health') {
      return
    }

    if (event === 'connect.challenge') {
      if (!this._connectSent) {
        this.sendConnect(payload)
      } else {
        console.log('connect 已主动发送，忽略服务端 challenge')
      }
      return
    }

    if (event === 'sessions.changed') {
      this._scopedDispatch('loadSessions')
      return
    }

    if (event === 'chat') {
      // 会话回包（aborted/error/final）先按 sessionKey 归属路由，避免旧 agent 的回包写入当前页面
      this._routingAgent = null
      if (payload) {
        const chatRoutingTarget = this._resolveRoutingTarget(
          payload,
          this._agentState
        )
        if (chatRoutingTarget === '__DROP__') {
          console.log(
            '[WS] skip chat event - cannot resolve agent for session:',
            payload.sessionKey
          )
          return
        }
        if (chatRoutingTarget) this._routingAgent = chatRoutingTarget
        if (process.env.NODE_ENV !== 'production') {
          const storeAgentId = this.store.state.cosmoClaw.currentAgentId
          const dbgSig = [
            payload.state,
            this.agentId,
            storeAgentId,
            this._routingAgent,
            payload.sessionKey || ''
          ].join('|')
          // delta 每帧都会触发，同签名只记一次，避免刷掉 trace 缓冲里的上下文
          if (dbgSig !== this._chatTraceSig) {
            this._chatTraceSig = dbgSig
            wsTrace('ws.chat', {
              chatState: payload.state,
              wsAgentId: this.agentId,
              storeAgentId,
              isCurrentAgent: this._isCurrentAgent,
              routingAgent: this._routingAgent,
              payloadSessionKey: payload.sessionKey || '',
              agentStateSessionKey: this._agentState.currentSessionKey || '',
              routingTarget: chatRoutingTarget
            })
          }
          // WS 绑定的 agent 与 store 不一致时，_agentState 会先按错误 agent 的会话判断，
          // 导致属于当前会话的回包被判为「其它会话」并只写入 agentData（页面不渲染）
          if (this.agentId !== storeAgentId) {
            wsDrift('ws.chat.agentMismatch', {
              chatState: payload.state,
              wsAgentId: this.agentId,
              storeAgentId,
              routingAgent: this._routingAgent,
              payloadSessionKey: payload.sessionKey || '',
              agentStateSessionKey: this._agentState.currentSessionKey || ''
            })
          }
        }
      }
      if (payload && payload.state === 'aborted') {
        const abortedSessionKey = this._resolveEventSessionKey(payload)
        this._clearStreamOwner(abortedSessionKey)
        this._scopedDispatch('finishStreamingMessage', {
          _sessionKey: abortedSessionKey
        })
      }
      if (payload && payload.state === 'error') {
        // 显示错误消息到聊天界面
        const errorText = payload.errorMessage || '请求失败'
        const errorKind = payload.errorKind || ''
        const displayText = errorKind
          ? `**[错误]** ${errorText} (${errorKind})`
          : `**[错误]** ${errorText}`
        const errorMsg = {
          id: generateUUID(),
          type: 'assistant',
          content: displayText,
          timestamp: Date.now(),
          isStreaming: false,
          isHistory: false,
          isError: true
        }
        this.store.commit('cosmoClaw/AGENT_MUTATION', {
          agentId: this._effectiveAgent,
          mutation: 'ADD_MESSAGE',
          payload: errorMsg,
          _routingActive: !!this._routingAgent,
          _sessionKey: this._resolveEventSessionKey(payload)
        })
        this._scopedDispatch('finishStreamingMessage', {
          _sessionKey: this._resolveEventSessionKey(payload)
        })
        return
      }
      if (payload && payload.state === 'final' && payload.message) {
        // chat final 是流式结束后的兜底消息，仅在以下情况渲染：
        // 1. agent 发生了错误（start → end 中间无内容）
        // 2. 流式内容未送达，需要 chat final 作为降级展示
        // 正常流式场景下内容已通过 agent→assistant 事件送达并 finalize
        const msg = payload.message
        let rawText = ''
        if (typeof msg.content === 'string') {
          rawText = msg.content
        } else if (Array.isArray(msg.content)) {
          rawText = msg.content
            .filter(c => c.type === 'text' && c.text)
            .map(c => c.text)
            .join('')
        }
        if (rawText) {
          const text = stripSilentMarkers(rawText)
          // 路由后读取事件所属会话的状态，避免用新页面的空会话误判内容缺失
          const ownerSessionKey = this._resolveEventSessionKey(payload)
          const curState =
            (ownerSessionKey && this._getSessionState(ownerSessionKey)) ||
            this._agentState
          // 检查是否有 agent 已送达的 assistant 内容
          // 如果有，说明流式已正常交付，跳过 chat final 避免重复
          const hasAgentContent = curState.messages.some(
            m => m.type === 'assistant' && !m.isHistory && m.content && m.content.length > 0
          )
          if (!hasAgentContent) {
            const assistantMsg = {
              id: generateUUID(),
              type: 'assistant',
              content: text,
              timestamp: msg.timestamp || Date.now(),
              isStreaming: false,
              isHistory: false
            }
            this.store.commit('cosmoClaw/AGENT_MUTATION', {
              agentId: this._effectiveAgent,
              mutation: 'ADD_MESSAGE',
              payload: assistantMsg,
              _routingActive: !!this._routingAgent,
              _sessionKey: this._resolveEventSessionKey(payload)
            })
          }
        }
      }
      if (payload && payload.state === 'final') {
        const finalSessionKey = this._resolveEventSessionKey(payload)
        this._clearStreamOwner(finalSessionKey)
        this._scopedDispatch('finishStreamingMessage', {
          _sessionKey: finalSessionKey
        })
      }
      return
    }

    if (event === 'agent') {
      this._handleAgentEvent(payload)
      return
    }

    // 日志：未被上述条件捕获的事件
    console.log(
      '[WS] unhandled event:',
      event,
      payload ? Object.keys(payload) : 'no payload'
    )
  }

  /**
   * 主动发送 connect 请求（不等服务端 challenge）
   * 连接建立后短暂等待服务端下发 challenge，若超时未收到则自行发起
   */
  sendConnectProactive() {
    setTimeout(() => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return
      if (!this._connectSent) {
        console.log('服务端未下发 connect.challenge，主动发送 connect 请求')
        this.sendConnect()
      }
    }, 300)
  }

  sendConnect(challengePayload) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return
    if (this._connectSent) {
      console.log('connect 请求已发送过，跳过重复发送')
      return
    }
    this._connectSent = true

    const connectReq = {
      type: 'req',
      id: generateUUID(),
      method: 'connect',
      params: {
        minProtocol: 3,
        maxProtocol: 3,
        client: {
          id: 'openclaw-control-ui',
          version: '2026.3.28',
          platform: 'Win32',
          mode: 'webchat',
          instanceId: getOrCreateInstanceId()
        },
        role: 'operator',
        scopes: [
          'operator.admin',
          'operator.read',
          'operator.write',
          'operator.approvals',
          'operator.pairing'
        ],
        caps: ['tool-events'],
        auth: {
          token: this.token || 'XcduMXuCBmsqkU2IIGNeAr9uSG5R3LpW'
        },
        userAgent: navigator.userAgent,
        locale: navigator.language || 'zh-CN'
      }
    }
    this.socket.send(JSON.stringify(connectReq))
  }

  _handleAgentEvent(payload) {
    // 清除上次的临时路由，重新根据当前事件计算
    this._routingAgent = null
    let state = this._agentState

    if (state.localDemoMode) {
      console.log('[WS] ignore agent event in local demo mode')
      return
    }

    // 已中止 run 的迟到事件：直接丢弃，避免污染原会话里新一轮的输出
    if (payload.runId && this._abortedRuns.has(String(payload.runId))) {
      return
    }

    // 统一归属解析：事件携带 sessionKey/runId 或可扫描到在途 run 时，
    // 一律路由回其真正所属的会话，避免旧会话的流式/回包内容写入新打开的初始页。
    const owner = this._resolveEventOwner(payload, state)
    if (owner.drop) {
      console.log(
        '[WS] skip event - sessionKey mismatch and agent not found:',
        payload.stream,
        'sessionKey:',
        payload.sessionKey
      )
      return
    }
    if (owner.routed && owner.agentId) {
      this._routingAgent = owner.agentId
      // 用事件归属的 session 读取对应会话的独立状态（避免串用当前会话的 state）
      const targetState = this._getSessionState(owner.sessionKey)
      if (targetState) state = targetState
      console.log(
        '[WS] routing event to agent:',
        owner.agentId,
        'stream:',
        payload.stream,
        'sessionKey:',
        payload.sessionKey,
        'ownerSessionKey:',
        owner.sessionKey
      )
    }

    // 事件最终归属的 sessionKey：优先采用归属解析结果，
    // 避免旧会话事件（尤其无 sessionKey 的增量）落到新会话
    const eventSessionKey = owner.sessionKey || state.currentSessionKey || null

    if (process.env.NODE_ENV !== 'production' && payload.stream !== 'assistant') {
      const storeAgentId = this.store.state.cosmoClaw.currentAgentId
      wsTrace('ws.agentEvent', {
        stream: payload.stream,
        phase: payload.data && payload.data.phase,
        wsAgentId: this.agentId,
        storeAgentId,
        isCurrentAgent: this._isCurrentAgent,
        routingAgent: this._routingAgent,
        payloadSessionKey: payload.sessionKey || '',
        agentStateSessionKey: (state && state.currentSessionKey) || '',
        eventSessionKey,
        owner
      })
      if (this.agentId !== storeAgentId) {
        wsDrift('ws.agentEvent.agentMismatch', {
          stream: payload.stream,
          phase: payload.data && payload.data.phase,
          wsAgentId: this.agentId,
          storeAgentId,
          routingAgent: this._routingAgent,
          payloadSessionKey: payload.sessionKey || '',
          eventSessionKey
        })
      }
    }

    // Record in-flight stream run ownership (agent + session) for fallback routing
    this._trackStreamOwner(payload, eventSessionKey)
    if (payload.runId) {
      this._scopedCommit('SET_CURRENT_RUN_ID', payload.runId, { _sessionKey: eventSessionKey })
    }

    const stream = payload.stream
    const phase = payload.data && payload.data.phase
    const streamData = payload.data || {}

    if (stream === 'lifecycle') {
      if (phase === 'start') {
        this._scopedCommit('SET_CURRENT_RUN_ID', payload.runId, { _sessionKey: eventSessionKey })
        this._scopedCommit('SET_GLOBAL_ERROR', null)
      } else if (phase === 'end') {
        this._clearStreamOwner(payload.sessionKey || payload.runId)
        this._scopedCommit('SET_AGENT_ENDED', true, { _sessionKey: eventSessionKey })
        this._scopedDispatch('finishStreamingMessage', {
          _sessionKey: this._resolveEventSessionKey(payload) || eventSessionKey
        })
      } else if (phase === 'error') {
        this._clearStreamOwner(payload.sessionKey || payload.runId)
        const errorText = (streamData && streamData.error) || 'Agent error'
        this._scopedCommit('SET_GLOBAL_ERROR', errorText)
        // 在聊天中显示错误消息
        const errorMsg = {
          id: generateUUID(),
          type: 'assistant',
          content: `**[错误]** ${errorText}`,
          timestamp: Date.now(),
          isStreaming: false,
          isHistory: false
        }
        this.store.commit('cosmoClaw/AGENT_MUTATION', {
          agentId: this._effectiveAgent,
          mutation: 'ADD_MESSAGE',
          payload: errorMsg,
          _routingActive: !!this._routingAgent,
          _sessionKey:
            this._resolveEventSessionKey(payload) ||
            state.currentSessionKey ||
            eventSessionKey ||
            null
        })
        this._scopedDispatch('finishStreamingMessage', {
          _sessionKey: this._resolveEventSessionKey(payload) || eventSessionKey
        })
      }
      return
    }

    if (stream === 'tool') {
      if (state.isAborting) return
      this._scopedCommit('SET_IS_STREAMING', true, { _sessionKey: eventSessionKey })

      const toolData = streamData
      const toolCallId = toolData.toolCallId || generateUUID()
      const toolName = toolData.name || toolData.toolName || 'unknown'
      // 工具自带的中间叙述：单独成行展示（对齐目标项目 intermediate 行）
      const toolMeta = toolData.meta && typeof toolData.meta === 'object' ? toolData.meta : {}
      const toolThought =
        toolData.thought || toolData.reasoning || toolMeta.thought || toolMeta.reasoning || ''
      if (phase === 'start') {
        const toolMsg = {
          id: generateUUID(),
          type: 'toolCall',
          content: `Calling tool: ${toolName}`,
          timestamp: Date.now(),
          toolName,
          toolCallId,
          runId: payload.runId || '',
          phase: 'start',
          args: toolData.args ? JSON.stringify(toolData.args, null, 2) : '',
          result: '',
          thought: toolThought,
          isStreaming: true,
          isError: false
        }
        this._scopedCommit('SPLIT_AT_TOOL_CALL', toolMsg, { _sessionKey: eventSessionKey })
      } else if (phase === 'update') {
        this._scopedCommit('UPDATE_TOOL_MESSAGE', {
          toolCallId,
          updates: {
            content: `Executing tool: ${toolName}...`,
            phase: 'update',
            ...(toolThought ? { thought: toolThought } : {})
          }
        }, { _sessionKey: eventSessionKey })
      } else if (phase === 'result') {
        const isError = toolData.isError || false
        const resultContent = toolData.result || toolData.meta || ''
        this._scopedCommit('UPDATE_TOOL_MESSAGE', {
          toolCallId,
          updates: {
            content: isError
              ? `Tool ${toolName} failed`
              : `Tool ${toolName} completed`,
            phase: 'result',
            isStreaming: false,
            isError,
            result: resultContent,
            meta: toolData.meta || '',
            ...(toolThought ? { thought: toolThought } : {})
          }
        }, { _sessionKey: eventSessionKey })

        if (resultContent && !isError) {
          const mediaRegex = /^MEDIA:(.+?)(?:\n|$)/gm
          const mediaSources = []
          let mm
          while ((mm = mediaRegex.exec(resultContent)) !== null) {
            mediaSources.push(mm[1])
          }
          if (mediaSources.length > 0) {
            const existingHasMedia = state.messages.some(
              (m) =>
                m.type === 'assistant' &&
                m.content &&
                m.content.includes('MEDIA:')
            )
            if (!existingHasMedia) {
              const mediaContent = mediaSources
                .map((s) => `MEDIA:${s}`)
                .join('\n')
              const mediaMsg = {
                id: generateUUID(),
                type: 'assistant',
                content: mediaContent,
                timestamp: Date.now(),
                isStreaming: false,
                isHistory: false
              }
              this._scopedCommit('ADD_MESSAGE', mediaMsg, { _sessionKey: eventSessionKey })
            }
          }
        }
      }
      return
    }

    if (stream === 'thinking') {
      if (state.isAborting) return
      const thinkingContent = streamData.thinking || ''
      if (thinkingContent) {
        const thinkingMsg = {
          id: generateUUID(),
          type: 'thinking',
          content: thinkingContent,
          timestamp: Date.now(),
          isHistory: false
        }
        this._scopedCommit('INSERT_BEFORE_STREAMING_ASSISTANT', thinkingMsg, { _sessionKey: eventSessionKey })
      }
      return
    }

    if (stream === 'assistant') {
      if (state.isAborting) return
      this._scopedCommit('SET_IS_STREAMING', true, { _sessionKey: eventSessionKey })

      const currentText = streamData.text || ''

      if (isPureSilentDelta(currentText)) {
        this._scopedCommit('SET_LAST_ASSISTANT_TEXT', currentText)
        return
      }
      if (isSilentTokenPrefix(currentText)) {
        this._scopedCommit('SET_LAST_ASSISTANT_TEXT', currentText)
        return
      }

      const finalContent = stripSilentMarkers(currentText)
      if (!finalContent) return

      const eventRunId = payload.runId || ''
      const mediaUrls = streamData.mediaUrls
      if (mediaUrls && mediaUrls.length > 0) {
        this._scopedCommit('SET_MEDIA_URLS', mediaUrls, { _sessionKey: eventSessionKey })
      }

      // 按会话独立追踪流式文本状态，确保 delta 计算不跨会话串用
      const sessionKey = payload.sessionKey || state.currentSessionKey || ''
      if (!this._sessionStreamState[sessionKey]) {
        this._sessionStreamState[sessionKey] = { lastProcessedText: null }
      }
      const streamState = this._sessionStreamState[sessionKey]

      if (streamState.lastProcessedText !== null) {
        // 已有基准文本 → 计算增量（仅新增部分）
        const baseText = streamState.lastProcessedText
        streamState.lastProcessedText = finalContent

        if (finalContent === baseText) return

        let delta = finalContent
        if (
          finalContent.length > baseText.length &&
          finalContent.startsWith(baseText)
        ) {
          delta = finalContent.substring(baseText.length)
        }

        // 找流式消息追加 delta
        let found = false
        for (let i = state.messages.length - 1; i >= 0; i--) {
          const m = state.messages[i]
          if (m.type === 'assistant' && m.isStreaming) {
            if (m._runId && eventRunId && m._runId !== eventRunId) continue
            this._scopedCommit(
              'UPDATE_LAST_MESSAGE_CONTENT',
              (m.content || '') + delta,
              { _sessionKey: eventSessionKey }
            )
            found = true
            break
          }
        }
        if (!found) {
          // 没有流式消息 → 用 delta 创建新消息（工具调用后的续写文本）
          const assistantMsg = {
            id: generateUUID(),
            type: 'assistant',
            content: delta,
            timestamp: Date.now(),
            isStreaming: true,
            isHistory: false,
            _runId: state.currentRunId || eventRunId || ''
          }
          this._scopedCommit('ADD_MESSAGE', assistantMsg, { _sessionKey: eventSessionKey })
        }

        if (payload.result && payload.result.summary === 'completed') {
          this._scopedDispatch('finishStreamingMessage', {
            _sessionKey: this._resolveEventSessionKey(payload) || eventSessionKey
          })
        }
        return
      }

      // 首次收到文本（或新会话/错误重置后）
      streamState.lastProcessedText = finalContent

      let found = false
      for (let i = state.messages.length - 1; i >= 0; i--) {
        const m = state.messages[i]
        if (m.type === 'assistant' && m.isStreaming) {
          if (m._runId && eventRunId && m._runId !== eventRunId) continue
          this._scopedCommit('UPDATE_LAST_MESSAGE_CONTENT', finalContent, { _sessionKey: eventSessionKey })
          found = true
          break
        }
      }

      if (!found) {
        // 没有流式消息 → 检查是否需新建（首次创建或工具调用后的兜底）
        let lastAssistantIdx = -1
        for (let i = state.messages.length - 1; i >= 0; i--) {
          if (state.messages[i].type === 'assistant') {
            lastAssistantIdx = i
            break
          }
        }
        if (
          lastAssistantIdx !== -1 &&
          state.messages[lastAssistantIdx].content === finalContent
        ) {
          return
        }
        if (
          eventRunId &&
          state.currentRunId &&
          eventRunId !== state.currentRunId
        ) {
          return
        }
        const assistantMsg = {
          id: generateUUID(),
          type: 'assistant',
          content: finalContent,
          timestamp: Date.now(),
          isStreaming: true,
          isHistory: false,
          _runId: state.currentRunId || eventRunId || ''
        }
        this._scopedCommit('ADD_MESSAGE', assistantMsg, { _sessionKey: eventSessionKey })
      }

      if (payload.result && payload.result.summary === 'completed') {
        this._scopedDispatch('finishStreamingMessage', {
          _sessionKey: this._resolveEventSessionKey(payload) || eventSessionKey
        })
      }
      return
    }
  }
}

export default CosmoClawWebSocket
